import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme-context";
import ErrorBoundary from "@/components/ErrorBoundary";

// Marketing + auth pages stay eager (small, and users almost always land here first).
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";

// Heavy / rarely-used routes are code-split for a smaller initial bundle.
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));

const DashboardOverview = lazy(() => import("@/pages/dashboard/Overview"));
const ProfilePage = lazy(() => import("@/pages/dashboard/Profile"));
const SettingsPage = lazy(() => import("@/pages/dashboard/Settings"));
const GeneratorPage = lazy(() => import("@/pages/dashboard/Generator"));
const ThumbnailStudioPage = lazy(() => import("@/pages/dashboard/ThumbnailStudio"));
const ImageStudioPage = lazy(() => import("@/pages/dashboard/ImageStudio"));
const VideosPage = lazy(() => import("@/pages/dashboard/Videos"));
const AnalyticsPage = lazy(() => import("@/pages/dashboard/Analytics"));
const TrendRadarPage = lazy(() => import("@/pages/dashboard/TrendRadar"));
const AIProvidersPage = lazy(() => import("@/pages/dashboard/AIProviders"));
const BillingPage = lazy(() => import("@/pages/dashboard/Billing"));

const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));

function RouteFallback() {
  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="route-fallback">
      <div className="flex items-center gap-3 text-muted-foreground">
        <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
        <p className="text-sm">Loading…</p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Emergent OAuth returns via URL fragment. Route it BEFORE anything else
  // to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Marketing */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* User dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardOverview />} />
          <Route path="generator" element={<GeneratorPage />} />
          <Route path="thumbnails" element={<ThumbnailStudioPage />} />
          <Route path="images" element={<ImageStudioPage />} />
          <Route path="trends" element={<TrendRadarPage />} />
          <Route path="videos" element={<VideosPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="ai-providers" element={<AIProvidersPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <DashboardLayout admin />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRouter />
              <Toaster richColors position="top-right" theme="dark" />
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </div>
    </ErrorBoundary>
  );
}
